// Considered: DraggableItemListMolecule (marketplace) — rejected because it hard-depends on
// `ChannelItem`'s `item_type`/`item_metadata`/`item_status` shape and has no keyboard path.
// Considered: SortEditorMolecule — rejected because it is drag-only (mouse) and couples its
// row markup to sort-clause fields. Both are designated future adopters of this molecule.
// See spec `atlas-view-editor-structure.md` Reuse Audit.
import React, { useCallback, useMemo, useState } from 'react';
import { useLiftAndMove } from '../../../hooks/useLiftAndMove';
import './ReorderableListMolecule.scss';

export interface ReorderableListLabels {
  /** aria-label on the list container. */
  listLabel: string;
  /** Accessible name of a row's reorder handle, e.g. "Reorder Name, position 1 of 4". */
  rowLabel: (index: number, total: number) => string;
  /** Live-region announcement when a keyboard lift starts. */
  liftedAnnouncement: (index: number, total: number) => string;
  /** Live-region announcement after each keyboard move. */
  movedAnnouncement: (index: number, total: number) => string;
  /** Live-region announcement when a lift is committed (drop). */
  droppedAnnouncement: (index: number, total: number) => string;
  /** Live-region announcement when a lift is cancelled (Escape). */
  cancelledAnnouncement: string;
}

export interface ReorderableListMoleculeProps<T> {
  items: T[];
  getKey: (item: T, index: number) => string;
  /** Renders a row's inner content. The molecule owns the row wrapper (drag + keyboard). */
  renderItem: (item: T, index: number, state: { isDragging: boolean; isLifted: boolean }) => React.ReactNode;
  /** Extra class name(s) merged onto the row wrapper, e.g. for a "selected" modifier. */
  getRowClassName?: (item: T, index: number, state: { isDragging: boolean; isLifted: boolean }) => string;
  /** Whether the item at `index` may be dragged / keyboard-lifted at all. Default: true. */
  isItemDraggable?: (item: T, index: number) => boolean;
  /** Whether moving the item at `from` to position `to` is legal (e.g. not past a locked row). */
  canMove: (from: number, to: number) => boolean;
  /**
   * Commits a move: relocate the item at `from` to position `to` (list-splice semantics).
   * `source` says which interaction produced it — a keyboard lift commits on EVERY Arrow
   * step, so a consumer that counts reorder intents must treat `'keyboard'` steps as
   * intermediate and count the lift once from `onLiftEnd`.
   */
  onReorder: (from: number, to: number, source: 'drag' | 'keyboard') => void;
  /**
   * Fires once when a KEYBOARD lift is dropped, with the net move it produced. Each Arrow
   * step already commits through `onReorder` so the list animates as the user moves, which
   * makes `onReorder` the wrong place to count a reorder *intent*. Not fired when the lift
   * is cancelled or when it nets zero. Drag and any consumer-owned controls report through
   * `onReorder` alone — they are already one call per intent.
   */
  onLiftEnd?: (from: number, to: number) => void;
  disabled?: boolean;
  className?: string;
  labels: ReorderableListLabels;
}

/**
 * Generic reorderable list: native HTML5 drag-and-drop on the row plus a keyboard
 * lift/move/drop/cancel interaction on a per-row HANDLE button.
 *
 * The rows are plain list items. A row holds the consumer's own controls (move buttons, a
 * select button), so it cannot itself be an interactive role — an `option` or `button`
 * with focusable descendants is invalid ARIA (axe `nested-interactive`). The keyboard path
 * is therefore its own control: a handle, named with `labels.rowLabel`, `aria-pressed`
 * while lifted, visually hidden until focused so the pointer layout is unchanged.
 */
function ReorderableListMolecule<T>({
  items,
  getKey,
  renderItem,
  getRowClassName,
  isItemDraggable,
  canMove,
  onReorder,
  onLiftEnd,
  disabled = false,
  className = '',
  labels,
}: Readonly<ReorderableListMoleculeProps<T>>): React.ReactElement {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const draggable = useCallback(
    (item: T, index: number) => !disabled && (isItemDraggable ? isItemDraggable(item, index) : true),
    [disabled, isItemDraggable]
  );

  const positions = useMemo(() => items.map((_, i) => i), [items]);
  const {
    announcement,
    isLifted,
    registerHandle,
    handleKeyDown,
    handleBlur,
  } = useLiftAndMove<number>({
    positions,
    // The lift is held as a position, but `items` can be reordered from OUTSIDE the molecule
    // (the consumer's own arrow buttons, an inspector edit that replaces the row object) —
    // `keyOf` re-reads the CURRENT occupant of a position on every check, so a stale lift
    // (something else now sits where the user's grab was) is detected rather than silently
    // retargeted.
    keyOf: (index) => getKey(items[index], index),
    isLiftable: (index) => draggable(items[index], index),
    canMove,
    onMove: onReorder,
    onLiftEnd,
    disabled,
    labels: {
      liftedAnnouncement: labels.liftedAnnouncement,
      movedAnnouncement: labels.movedAnnouncement,
      droppedAnnouncement: labels.droppedAnnouncement,
      cancelledAnnouncement: labels.cancelledAnnouncement,
    },
  });

  const handleDragStart = useCallback(
    (e: React.DragEvent, item: T, index: number) => {
      if (!draggable(item, index)) {
        e.preventDefault();
        return;
      }
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));
      setDragIndex(index);
    },
    [draggable]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      if (disabled || dragIndex === null) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = canMove(dragIndex, index) ? 'move' : 'none';
      setOverIndex(index);
    },
    [disabled, dragIndex, canMove]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      const from = dragIndex;
      setDragIndex(null);
      setOverIndex(null);
      if (disabled || from === null || from === index) return;
      if (!canMove(from, index)) return;
      onReorder(from, index, 'drag');
    },
    [disabled, dragIndex, canMove, onReorder]
  );

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setOverIndex(null);
  }, []);

  // Ignore keydowns that bubbled up from a nested interactive descendant (e.g. an
  // arrow-move button rendered by `renderItem`) — only a keydown targeting the handle
  // itself drives the lift/move/drop/cancel state machine.
  const handleHandleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (e.target !== e.currentTarget) return;
      handleKeyDown(e, index);
    },
    [handleKeyDown]
  );

  return (
    <div className={`reorderable-list-molecule ${className}`.trim()}>
      {/* Outside the list element: every child of a list must be a list item. */}
      <div className="reorderable-list-molecule__announcer" aria-live="polite">{announcement}</div>
      <ul className="reorderable-list-molecule__list" aria-label={labels.listLabel}>
      {items.map((item, index) => {
        const isDragging = dragIndex === index;
        const isOver = overIndex === index && dragIndex !== null && dragIndex !== index;
        const lifted = isLifted(index);
        const state = { isDragging, isLifted: lifted };
        const isRowReorderable = draggable(item, index);
        const rowKey = getKey(item, index);
        const rowClasses = [
          'reorderable-list-molecule__row',
          isDragging ? 'reorderable-list-molecule__row--dragging' : '',
          isOver ? 'reorderable-list-molecule__row--over' : '',
          lifted ? 'reorderable-list-molecule__row--lifted' : '',
          getRowClassName ? getRowClassName(item, index, state) : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <li
            key={rowKey}
            className={rowClasses}
            draggable={isRowReorderable}
            onDragStart={(e) => handleDragStart(e, item, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            {isRowReorderable && (
              <button
                type="button"
                className="reorderable-list-molecule__handle"
                ref={(el) => registerHandle(rowKey, el)}
                aria-label={labels.rowLabel(index, items.length)}
                // Pressed while lifted: the lift is a held state, and `aria-grabbed` is
                // deprecated in ARIA 1.2. The live region announces each move.
                aria-pressed={lifted}
                disabled={disabled}
                onKeyDown={(e) => handleHandleKeyDown(e, index)}
                onBlur={() => handleBlur(rowKey)}
              >
                <i className="pi pi-bars" aria-hidden="true" />
              </button>
            )}
            {renderItem(item, index, state)}
          </li>
        );
      })}
      </ul>
    </div>
  );
}

export default ReorderableListMolecule;
